// PlanAnualPanel.js — Sección 8 del dashboard financiero: una fila por año
// con los objetivos contra los que se compara "vamos por delante o por
// detrás del plan". El reparto mensual se calcula solo según la
// estacionalidad del año anterior (resumenFinancieroMensual) y admite
// ajustar un mes a mano sin descuadrar el resto del año — ver
// netlify/functions/_planFinanciero.js.
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';

const CAMPOS = [
  { key: 'facturacionConIva', label: 'Facturación con IVA' },
  { key: 'facturacionNeta',   label: 'Facturación neta' },
  { key: 'margenMinimoPct',   label: 'Margen mínimo (%)' },
  { key: 'margenObjetivoPct', label: 'Margen objetivo (%)' },
  { key: 'numTecnicos',       label: 'Nº técnicos' },
  { key: 'contratosVigentes', label: 'Contratos vigentes (fin de año)' },
  { key: 'pctRecurrente',     label: '% ingreso recurrente' },
];

function fmtEur(n) { return Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'; }
function mesLabel(mesStr) {
  const [y, m] = mesStr.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
}

export default function PlanAnualPanel() {
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(null); // anio en edición, o null
  const [form, setForm] = useState({});
  const [anioExpandido, setAnioExpandido] = useState(null);
  const [ajuste, setAjuste] = useState({}); // { mes: valor } en edición para el año expandido

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    fetch('/.netlify/functions/getConfigPlan', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => { if (!data.success) { setError(data.error); return; } setPlanes(data.planes); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const empezarEdicion = (plan) => {
    setEditando(plan ? plan.anio : new Date().getFullYear());
    setForm(plan || { anio: new Date().getFullYear(), facturacionConIva: '', facturacionNeta: '', margenMinimoPct: 20, margenObjetivoPct: 25, numTecnicos: '', contratosVigentes: '', pctRecurrente: '' });
  };

  const guardar = async () => {
    try {
      const res = await fetch('/.netlify/functions/saveConfigPlan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(Object.fromEntries(['anio', ...CAMPOS.map(c => c.key)].map(k => [k, Number(form[k])]))),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setEditando(null);
      cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  const guardarAjusteMes = async (anio, mes) => {
    const valor = ajuste[mes];
    try {
      const res = await fetch('/.netlify/functions/saveConfigPlan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ accion: 'ajustarMes', anio, mes, valor: valor === '' ? null : Number(valor) }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setAjuste(a => { const n = { ...a }; delete n[mes]; return n; });
      cargar();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>;

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

      <div className="flex justify-end">
        <button onClick={() => empezarEdicion(null)} className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700">+ Nuevo año</button>
      </div>

      {editando != null && (
        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <h3 className="font-bold text-gray-900 text-sm mb-3">Plan {editando}</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">Año</label>
              <input type="number" className="border rounded-lg px-3 py-2 text-sm w-full" value={form.anio} onChange={e => setForm(f => ({ ...f, anio: e.target.value }))} />
            </div>
            {CAMPOS.map(c => (
              <div key={c.key}>
                <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">{c.label}</label>
                <input type="number" className="border rounded-lg px-3 py-2 text-sm w-full" value={form[c.key] ?? ''} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={guardar} className="px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700">Guardar</button>
            <button onClick={() => setEditando(null)} className="px-4 py-2 bg-gray-100 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-200">Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50/60">
                <th className="text-left px-4 py-2 font-bold text-gray-500 uppercase">Año</th>
                {CAMPOS.map(c => <th key={c.key} className="text-right px-3 py-2 font-bold text-gray-500 uppercase whitespace-nowrap">{c.label}</th>)}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {planes.map(p => (
                <React.Fragment key={p.anio}>
                  <tr>
                    <td className="px-4 py-2 font-bold text-gray-900">{p.anio}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(p.facturacionConIva)}</td>
                    <td className="px-3 py-2 text-right">{fmtEur(p.facturacionNeta)}</td>
                    <td className="px-3 py-2 text-right">{p.margenMinimoPct}%</td>
                    <td className="px-3 py-2 text-right">{p.margenObjetivoPct}%</td>
                    <td className="px-3 py-2 text-right">{p.numTecnicos}</td>
                    <td className="px-3 py-2 text-right">{p.contratosVigentes}</td>
                    <td className="px-3 py-2 text-right">{p.pctRecurrente}%</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setAnioExpandido(a => a === p.anio ? null : p.anio)} className="text-blue-600 hover:underline mr-2">
                        {anioExpandido === p.anio ? 'Ocultar meses' : 'Ver reparto mensual'}
                      </button>
                      <button onClick={() => empezarEdicion(p)} className="text-gray-600 hover:underline">Editar</button>
                    </td>
                  </tr>
                  {anioExpandido === p.anio && p.repartoMensual && (
                    <tr>
                      <td colSpan={9} className="px-4 py-3 bg-gray-50/60">
                        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
                          {p.repartoMensual.map((valor, i) => {
                            const mesStr = String(i + 1).padStart(2, '0');
                            const ajustado = p.overridesMensuales && Object.prototype.hasOwnProperty.call(p.overridesMensuales, mesStr);
                            return (
                              <div key={mesStr} className={`rounded-lg border px-2 py-2 ${ajustado ? 'border-amber-300 bg-amber-50' : 'border-gray-100 bg-white'}`}>
                                <p className="text-[10px] font-bold text-gray-400 uppercase">{mesLabel(`${p.anio}-${mesStr}`)}</p>
                                <input
                                  type="number"
                                  className="w-full border rounded px-1.5 py-1 text-xs mt-1"
                                  value={ajuste[mesStr] ?? Math.round(valor)}
                                  onChange={e => setAjuste(a => ({ ...a, [mesStr]: e.target.value }))}
                                  onBlur={() => { if (ajuste[mesStr] != null && Number(ajuste[mesStr]) !== Math.round(valor)) guardarAjusteMes(p.anio, mesStr); }}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2">Ámbar = ajustado a mano. El resto se reparte solo, según la estacionalidad del año anterior, para que la suma siga cuadrando con la facturación neta del plan.</p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {planes.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-gray-400">Sin años en el plan todavía</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
